import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { saveConsultation, fetchConsultation } from "../../services/api";
import "./FichePatient.css";

export default function FichePatient({
  defaultMeeting = {
    id: "mtg_101",
    datetime: new Date().toISOString(),
    patient: { id: "p_1", name: "Amel Ben Salem", email: "amel@example.com" },
    doctor: { id: "d_1", name: "Dr. Skima" },
  },
  initialData = null,
  onSaveDraft = (payload) => alert("Saved draft:\n" + JSON.stringify(payload, null, 2)),
  onFinalize = (payload) => alert("Finalized:\n" + JSON.stringify(payload, null, 2)),
}) {
  const { state } = useLocation();

  const { token, API_BASE } = useAuth();
  const meeting = state?.meeting || defaultMeeting;

  // Transfer State
  const [showTransfer, setShowTransfer] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);

  useEffect(() => {
    if (showTransfer && doctors.length === 0) {
      fetch(`${API_BASE}/doctor/all`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(setDoctors)
        .catch(console.error);
    }
  }, [showTransfer, API_BASE, token, doctors.length]);

  const handleTransfer = async () => {
    if (!selectedDoctor) return alert("Select a doctor");
    setIsTransferring(true);
    try {
      const res = await fetch(`${API_BASE}/affectations/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: meeting.patient.id, // Ensure this maps correctly to DB ID
          new_doctor_id: selectedDoctor,
          motif: transferReason
        })
      });
      const data = await res.json();
      if (data.success) {
        alert("Patient transferred successfully.");
        // Close modal and maybe navigate away as patient is no longer ours?
        // navigate('/dashboard'); // or refresh
        setShowTransfer(false);
      } else {
        alert("Transfer failed: " + data.error);
      }
    } catch (e) {
      console.error(e);
      alert("Error during transfer");
    } finally {
      setIsTransferring(false);
    }
  };

  // ---- 1) Informations patient (prérempli depuis meeting si dispo)
  const [patientInfo, setPatientInfo] = useState({
    nom: meeting?.patient?.name || "",
    prenom: "",
    dateNaissance: "",
    adresse: "",
    cp: "",
    ville: "",
    telephone: "",
    email: meeting?.patient?.email || "",
  });

  // ---- 2) Motif de la demande
  const [motif, setMotif] = useState({
    arreter: false,
    reduire: false,
    infos: false,
    aiderAutre: false,
    autre: "",
  });

  // ---- 3) Traitement prévu/en cours
  const [traitement, setTraitement] = useState({
    dejaCommence: "non", // oui | non
    depuisQuand: "",
    substituts: false,   // patchs, gommes, etc.
    medicaments: false,  // Champix®, Zyban®, etc.
    suiviPsy: false,     // psy / tabacologue
    autre: "",
    typePrecisions: "",
  });

  // ---- E-cigarette / nicotine liquide
  const [vapo, setVapo] = useState({
    utiliseVapo: "non", // oui | non
    volumeParSemaine: "",
    flaconsNicotine: "non", // oui | non
    dosageMgMl: "",
  });

  // ---- 5) Test d’addiction à la nicotine (Fagerström simplifié)
  // 6 items, on calcule un score total 0–10
  const [fager, setFager] = useState({
    q1: "", // Première cigarette après réveil
    q2: "", // Difficulté à s’abstenir
    q3: "", // Cigarette la plus difficile à abandonner
    q4: "", // Nb de cigarettes/jour
    q5: "", // Fume davantage le matin
    q6: "", // Fume en cas de maladie
  });

  // ---- autres champs existants utiles
  const [consent, setConsent] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // hydrate (mode édition)
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;
    if (d.patientInfo) setPatientInfo((v) => ({ ...v, ...d.patientInfo }));
    if (d.motif) setMotif((v) => ({ ...v, ...d.motif }));
    if (d.traitement) setTraitement((v) => ({ ...v, ...d.traitement }));
    if (d.vapo) setVapo((v) => ({ ...v, ...d.vapo }));
    if (d.fager) setFager((v) => ({ ...v, ...d.fager }));
    if (typeof d.consent === "boolean") setConsent(d.consent);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id]);

  // ---- Scoring Fagerström
  // barème standard:
  // q1 (min à la 1ère cig) : ≤5m=3, 6–30m=2, 31–60m=1, >60m=0
  // q2 (difficile de s’abstenir) : oui=1, non=0
  // q3 (plus dure à abandonner) : matin=1, autres=0
  // q4 (cigs/jour) : 10–20=1, 21–30=2, ≥31=3, ≤10=0
  // q5 (plus le matin) : oui=1, non=0
  // q6 (même malade) : oui=1, non=0
  const fagerScore = useMemo(() => {
    let s = 0;
    if (fager.q1 === "≤5") s += 3;
    else if (fager.q1 === "6–30") s += 2;
    else if (fager.q1 === "31–60") s += 1;

    if (fager.q2 === "oui") s += 1;
    if (fager.q3 === "matin") s += 1;

    if (fager.q4 === "10-20") s += 1;
    else if (fager.q4 === "21-30") s += 2;
    else if (fager.q4 === "≥31") s += 3;

    if (fager.q5 === "oui") s += 1;
    if (fager.q6 === "oui") s += 1;
    return s;
  }, [fager]);

  function validate(finalize = false) {
    if (!consent) return "Le consentement du patient est requis.";
    if (finalize) {
      // exiger au moins un motif ou un champ clé
      const aUnMotif = motif.arreter || motif.reduire || motif.infos || motif.aiderAutre || motif.autre.trim();
      if (!aUnMotif) return "Veuillez sélectionner le motif de la demande.";
    }
    return "";
  }

  const { id: appointmentId } = useParams();

  // Load existing data
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (appointmentId) {
      fetchConsultation(appointmentId).then(cons => {
        if (cons && cons.data) {
          const d = cons.data;
          if (d.patientInfo) setPatientInfo((v) => ({ ...v, ...d.patientInfo }));
          if (d.motif) setMotif((v) => ({ ...v, ...d.motif }));
          if (d.traitement) setTraitement((v) => ({ ...v, ...d.traitement }));
          if (d.vapo) setVapo((v) => ({ ...v, ...d.vapo }));
          if (d.fager) setFager((v) => ({ ...v, ...d.fager }));
          if (typeof d.consent === "boolean") setConsent(d.consent);
        }
      }).catch(console.error);
    }
  }, [appointmentId]);

  // .... (rest of the component state) ....

  function packPayload(status = "draft") {
    return {
      status,
      appointment_id: appointmentId || meeting.id,
      patient_id: meeting.patient.id,
      data: {
        patientInfo,
        motif,
        traitement,
        vapo,
        fager,
        fagerScore,
        consent
      }
    };
  }

  async function handleSaveDraft(e) {
    e.preventDefault();
    const v = validate(false);
    if (v) return setErr(v);
    setErr("");
    setSaving(true);
    try {
      const payload = packPayload("draft");
      await saveConsultation(payload);
      alert("Draft saved successfully.");
      onSaveDraft(payload);
    } catch (err) {
      console.error(err);
      alert("Failed to save draft.");
    } finally {
      setSaving(false);
    }
  }

  async function handleFinalize(e) {
    e.preventDefault();
    const v = validate(true);
    if (v) return setErr(v);
    setErr("");
    setFinalizing(true);
    try {
      const payload = packPayload("final");
      await saveConsultation(payload);
      alert("Consultation finalized and saved.");
      onFinalize(payload);
    } catch (err) {
      console.error(err);
      alert("Failed to finalize.");
    } finally {
      setFinalizing(false);
    }
  }

  const fmtDate = (iso) => new Date(iso).toLocaleString();

  return (
    <div className="fp-shell">
      {/* Header */}
      <div className="fp-head">
        <div>
          <h1 className="fp-title">Dossier de consultation de tabacologie</h1>
          <p className="fp-sub">Rendez-vous : {fmtDate(meeting.datetime)}</p>
        </div>
        <div className="fp-patient">
          <span className="fp-avatar">
            {meeting.patient.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
          </span>
          <div>
            <div className="fp-pname">{meeting.patient.name}</div>
            <div className="fp-muted">{meeting.patient.email}</div>
          </div>
        </div>
      </div>

      <form className="fp-form" onSubmit={(e) => e.preventDefault()}>
        {/* 1. Informations sur le patient */}
        <section className="fp-card">
          <div className="fp-card-title">1. Informations sur le patient</div>
          <div className="fp-grid-3">
            <div className="fp-field">
              <label className="fp-label">Nom</label>
              <input className="fp-input" value={patientInfo.nom}
                onChange={(e) => setPatientInfo({ ...patientInfo, nom: e.target.value })} />
            </div>
            <div className="fp-field">
              <label className="fp-label">Prénom</label>
              <input className="fp-input" value={patientInfo.prenom}
                onChange={(e) => setPatientInfo({ ...patientInfo, prenom: e.target.value })} />
            </div>
            <div className="fp-field">
              <label className="fp-label">Date de naissance</label>
              <input type="date" className="fp-input" value={patientInfo.dateNaissance}
                onChange={(e) => setPatientInfo({ ...patientInfo, dateNaissance: e.target.value })} />
            </div>
          </div>

          <div className="fp-grid-3">
            <div className="fp-field">
              <label className="fp-label">Adresse</label>
              <input className="fp-input" value={patientInfo.adresse}
                onChange={(e) => setPatientInfo({ ...patientInfo, adresse: e.target.value })} />
            </div>
            <div className="fp-field">
              <label className="fp-label">Code postal</label>
              <input className="fp-input" value={patientInfo.cp}
                onChange={(e) => setPatientInfo({ ...patientInfo, cp: e.target.value })} />
            </div>
            <div className="fp-field">
              <label className="fp-label">Ville</label>
              <input className="fp-input" value={patientInfo.ville}
                onChange={(e) => setPatientInfo({ ...patientInfo, ville: e.target.value })} />
            </div>
          </div>

          <div className="fp-grid-2">
            <div className="fp-field">
              <label className="fp-label">Téléphone</label>
              <input className="fp-input" value={patientInfo.telephone}
                onChange={(e) => setPatientInfo({ ...patientInfo, telephone: e.target.value })} />
            </div>
            <div className="fp-field">
              <label className="fp-label">Adresse e-mail</label>
              <input type="email" className="fp-input" value={patientInfo.email}
                onChange={(e) => setPatientInfo({ ...patientInfo, email: e.target.value })} />
            </div>
          </div>
        </section>

        {/* 2. Motif de la demande */}
        <section className="fp-card">
          <div className="fp-card-title">2. Motif de la demande</div>
          <div className="fp-list">
            <label className="fp-check">
              <input type="checkbox" checked={motif.arreter}
                onChange={(e) => setMotif({ ...motif, arreter: e.target.checked })} />
              <span>Je voudrais arrêter de fumer</span>
            </label>
            <label className="fp-check">
              <input type="checkbox" checked={motif.reduire}
                onChange={(e) => setMotif({ ...motif, reduire: e.target.checked })} />
              <span>Je voudrais réduire ma consommation de tabac</span>
            </label>
            <label className="fp-check">
              <input type="checkbox" checked={motif.infos}
                onChange={(e) => setMotif({ ...motif, infos: e.target.checked })} />
              <span>Je veux obtenir des renseignements sur le sevrage tabagique</span>
            </label>
            <label className="fp-check">
              <input type="checkbox" checked={motif.aiderAutre}
                onChange={(e) => setMotif({ ...motif, aiderAutre: e.target.checked })} />
              <span>Je veux aider un proche à arrêter</span>
            </label>

            <div className="fp-field">
              <label className="fp-label">Autre (préciser)</label>
              <input className="fp-input" value={motif.autre}
                onChange={(e) => setMotif({ ...motif, autre: e.target.value })} />
            </div>
          </div>
        </section>

        {/* 3. Traitement prévu / en cours */}
        <section className="fp-card">
          <div className="fp-card-title">3. Traitement prévu ou en cours</div>

          <div className="fp-field">
            <div className="fp-label">Avez-vous déjà commencé un traitement de sevrage tabagique ?</div>
            <div className="fp-seg">
              {["oui", "non"].map((v) => (
                <button key={v} type="button"
                  className={`fp-seg-btn ${traitement.dejaCommence === v ? "is-active" : ""}`}
                  onClick={() => setTraitement({ ...traitement, dejaCommence: v })}>
                  {v.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {traitement.dejaCommence === "oui" && (
            <div className="fp-grid-2">
              <div className="fp-field">
                <label className="fp-label">Depuis quand ?</label>
                <input className="fp-input" placeholder="ex. 2 mois"
                  value={traitement.depuisQuand}
                  onChange={(e) => setTraitement({ ...traitement, depuisQuand: e.target.value })} />
              </div>
              <div className="fp-field">
                <label className="fp-label">Précisions sur le type</label>
                <input className="fp-input" placeholder="détails"
                  value={traitement.typePrecisions}
                  onChange={(e) => setTraitement({ ...traitement, typePrecisions: e.target.value })} />
              </div>
            </div>
          )}

          <div className="fp-list fp-list-inline">
            <label className="fp-check">
              <input type="checkbox" checked={traitement.substituts}
                onChange={(e) => setTraitement({ ...traitement, substituts: e.target.checked })} />
              <span>Substituts nicotiniques (patchs, gommes, etc.)</span>
            </label>
            <label className="fp-check">
              <input type="checkbox" checked={traitement.medicaments}
                onChange={(e) => setTraitement({ ...traitement, medicaments: e.target.checked })} />
              <span>Traitements médicamenteux (Champix®, Zyban®, etc.)</span>
            </label>
            <label className="fp-check">
              <input type="checkbox" checked={traitement.suiviPsy}
                onChange={(e) => setTraitement({ ...traitement, suiviPsy: e.target.checked })} />
              <span>Suivi psychologique / tabacologue</span>
            </label>
            <div className="fp-field" style={{ marginTop: 8 }}>
              <label className="fp-label">Autre (préciser)</label>
              <input className="fp-input" value={traitement.autre}
                onChange={(e) => setTraitement({ ...traitement, autre: e.target.value })} />
            </div>
          </div>

          <div className="fp-divider" />

          <div className="fp-field">
            <div className="fp-label">VAPORISATEUR PERSONNEL (cigarette électronique)</div>
            <div className="fp-seg">
              {["oui", "non"].map((v) => (
                <button key={v} type="button"
                  className={`fp-seg-btn ${vapo.utiliseVapo === v ? "is-active" : ""}`}
                  onClick={() => setVapo({ ...vapo, utiliseVapo: v })}>
                  {v.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {vapo.utiliseVapo === "oui" && (
            <>
              <div className="fp-grid-2">
                <div className="fp-field">
                  <label className="fp-label">Volume vapoté / semaine</label>
                  <input className="fp-input" placeholder="ex. 21 mL"
                    value={vapo.volumeParSemaine}
                    onChange={(e) => setVapo({ ...vapo, volumeParSemaine: e.target.value })} />
                </div>
                <div className="fp-field">
                  <label className="fp-label">Flacons avec nicotine ?</label>
                  <div className="fp-seg">
                    {["oui", "non"].map((v) => (
                      <button key={v} type="button"
                        className={`fp-seg-btn ${vapo.flaconsNicotine === v ? "is-active" : ""}`}
                        onClick={() => setVapo({ ...vapo, flaconsNicotine: v })}>
                        {v.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {vapo.flaconsNicotine === "oui" && (
                <div className="fp-field">
                  <label className="fp-label">Dosage nicotine (mg/mL)</label>
                  <input className="fp-input" placeholder="ex. 12"
                    value={vapo.dosageMgMl}
                    onChange={(e) => setVapo({ ...vapo, dosageMgMl: e.target.value })} />
                </div>
              )}
            </>
          )}
        </section>

        {/* 5. Test d’addiction à la nicotine */}
        <section className="fp-card">
          <div className="fp-card-title">5. Test d’addiction à la nicotine</div>

          <div className="fp-qa">
            <div className="fp-q">
              <div className="fp-label">Temps jusqu’à la 1ère cigarette après le réveil</div>
              <div className="fp-radio-row">
                {["≤5", "6–30", "31–60", ">60"].map((v) => (
                  <label key={v} className="fp-radio">
                    <input type="radio" name="q1" checked={fager.q1 === v} onChange={() => setFager({ ...fager, q1: v })} />
                    <span>{v} min</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="fp-q">
              <div className="fp-label">Difficulté à s’abstenir dans les lieux interdits</div>
              <div className="fp-radio-row">
                {["oui", "non"].map((v) => (
                  <label key={v} className="fp-radio">
                    <input type="radio" name="q2" checked={fager.q2 === v} onChange={() => setFager({ ...fager, q2: v })} />
                    <span>{v.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="fp-q">
              <div className="fp-label">Cigarette la plus difficile à abandonner</div>
              <div className="fp-radio-row">
                {["matin", "autre"].map((v) => (
                  <label key={v} className="fp-radio">
                    <input type="radio" name="q3" checked={fager.q3 === v} onChange={() => setFager({ ...fager, q3: v })} />
                    <span>{v === "matin" ? "La première du matin" : "Une autre"}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="fp-q">
              <div className="fp-label">Cigarettes par jour</div>
              <div className="fp-radio-row">
                {["≤10", "10-20", "21-30", "≥31"].map((v) => (
                  <label key={v} className="fp-radio">
                    <input type="radio" name="q4" checked={fager.q4 === v} onChange={() => setFager({ ...fager, q4: v })} />
                    <span>{v}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="fp-q">
              <div className="fp-label">Fumez-vous davantage le matin ?</div>
              <div className="fp-radio-row">
                {["oui", "non"].map((v) => (
                  <label key={v} className="fp-radio">
                    <input type="radio" name="q5" checked={fager.q5 === v} onChange={() => setFager({ ...fager, q5: v })} />
                    <span>{v.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="fp-q">
              <div className="fp-label">Fumez-vous même en cas de maladie (au lit) ?</div>
              <div className="fp-radio-row">
                {["oui", "non"].map((v) => (
                  <label key={v} className="fp-radio">
                    <input type="radio" name="q6" checked={fager.q6 === v} onChange={() => setFager({ ...fager, q6: v })} />
                    <span>{v.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="fp-bmi">
            <span className="fp-label">Score Fagerström</span>
            <span className="fp-badge">{Number.isFinite(fagerScore) ? fagerScore : "—"}</span>
          </div>
        </section>

        {/* Consent + actions */}
        <section className="fp-actions-wrap">
          <label className="fp-check">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>Je confirme le consentement du patient à enregistrer ces informations.</span>
          </label>

          {err && <div className="fp-error">{err}</div>}

          <div className="fp-actions">
            <button className="fp-btn fp-btn-ghost" onClick={handleSaveDraft} disabled={saving}>
              {saving ? "Enregistrement…" : "Enregistrer le brouillon"}
            </button>
            <button className="fp-btn fp-btn-primary" onClick={handleFinalize} disabled={finalizing}>
              {finalizing ? "Finalisation…" : "Finaliser & sauvegarder"}
            </button>
            <button className="fp-btn fp-btn-danger" style={{ marginLeft: 'auto' }} onClick={() => setShowTransfer(true)} type="button">
              Transfer Patient
            </button>
          </div>
        </section>
      </form>

      {/* Transfer Modal */}
      {showTransfer && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Transfer Patient</h3>
            <p className="muted">Assign this patient to another specialist.</p>
            <div className="modal-grid single">
              <label>
                Select Specialist
                <select value={selectedDoctor} onChange={e => setSelectedDoctor(e.target.value)}>
                  <option value="">-- Choose Doctor --</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>Dr. {d.first_name} {d.last_name} ({d.specialty})</option>
                  ))}
                </select>
              </label>
              <label>
                Reason for Transfer
                <input type="text" value={transferReason} onChange={e => setTransferReason(e.target.value)} placeholder="e.g. Requires Psychiatry" />
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn danger" onClick={handleTransfer} disabled={isTransferring}>
                {isTransferring ? "Transferring..." : "Confirm Transfer"}
              </button>
              <button className="btn ghost" onClick={() => setShowTransfer(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
